import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { UsuarioPF } from '@vabembarqueja/embarque-ja-shared-components';
import { useCallback, useEffect, useState } from 'react';
import { Controller, FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form';

import AssociarError from '../../../assets/associar-error.svg';
import AssociarFuncionario from '../../../assets/associar-funcionario.svg';
import {
  ASSOCIACAO_FUNCIONARIO_DEFAULT,
  AssociacaoFuncionario,
  associacaoFuncionarioFormModelSchema
} from '../../../models/AssociacaoFuncionario.model';
import FuncionarioService from '../../../services/Funcionario.service';
import { Colors } from '../../../shared/colors';
import FeedbackDrawer from '../../components/FeedbackDrawer';
import { Input } from '../../components/Form/Input';
import { PatternInput } from '../../components/Form/PatternInput';
import Loading from '../../components/Loading';
import { Switch } from '../../components/Switch';

import FormCartao from './FormCartao';

interface Props {
  onClose: () => void;
}

const FormAssociarUsuario = ({ onClose }: Props) => {
  const [desabilitarCampos, setDesabilitarCampos] = useState(true);
  const [cadastroNovo, setCadastroNovo] = useState(false);
  const [errorMSg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  const form = useForm<AssociacaoFuncionario>({
    defaultValues: ASSOCIACAO_FUNCIONARIO_DEFAULT,
    resolver: yupResolver(associacaoFuncionarioFormModelSchema)
  });

  const cpf = form.watch('cpf');
  const associarCartao = form.watch('associarCartao');

  const onValid = (data: AssociacaoFuncionario) => {
    if (cadastroNovo) {
      if (!data.confirmarEmail) {
        form.setError('confirmarEmail', { message: 'Campo obrigatório' });
        return;
      }

      if (data.email !== data.confirmarEmail) {
        form.setError('confirmarEmail', { message: 'Os e-mails informados não conferem.' });
        return;
      }

      form.clearErrors('confirmarEmail');
    }

    const payload = { ...data };
    delete payload.confirmarEmail;

    setLoading(true);
    FuncionarioService.criar(payload)
      .then(() => setCompleted(true))
      .catch((err) => {
        setErrorMsg(err.message);
      })
      .finally(() => setLoading(false));
  };

  const onInvalid: SubmitErrorHandler<AssociacaoFuncionario> = (err) => {
    console.log(err);
  };

  const setCamposUsuario = useCallback(
    (func: UsuarioPF | null) => {
      if (func) {
        form.setValue('email', func.email);
        form.setValue('nome', func.nome);
        form.setValue('confirmarEmail', '');
        form.clearErrors('confirmarEmail');
        setDesabilitarCampos(true);
        setCadastroNovo(false);
      } else {
        form.setValue('nome', '');
        form.setValue('email', '');
        form.setValue('confirmarEmail', '');
        form.clearErrors('confirmarEmail');
        setDesabilitarCampos(false);
        setCadastroNovo(true);
      }
    },
    [form]
  );

  useEffect(() => {
    form.setValue('numeroCartao', '');
    form.clearErrors(['numeroCartao']);
  }, [associarCartao, form]);

  useEffect(() => {
    if (cpf.replace(/\D/g, '').length !== 11) {
      form.clearErrors('cpf');
      setCadastroNovo(false);
      return;
    }

    form.trigger('cpf').then((cpfValido) => {
      if (cpfValido) FuncionarioService.buscar(cpf).then(setCamposUsuario);
      else {
        setDesabilitarCampos(true);
        setCadastroNovo(false);
      }
    });
  }, [form, cpf, setCamposUsuario]);

  if (completed)
    return (
      <FeedbackDrawer
        titulo="Funcionário associado!"
        subtitulo="Funcionário associado com sucesso!"
        textoBotao="Fechar"
        onClick={onClose}
        imagemModal={AssociarFuncionario}
      />
    );

  if (errorMSg)
    return (
      <FeedbackDrawer
        titulo="Erro ao associar!"
        subtitulo={errorMSg}
        textoBotao="Fechar"
        onClick={onClose}
        imagemModal={AssociarError}
      />
    );

  if (loading) return <Loading show={loading} bgColor="white" />;

  return (
    <FormProvider {...form}>
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        minHeight={0}
        sx={{
          '& .MuiInputLabel-root': {
            color: '#283C58',
            fontSize: 15,
            fontWeight: 500,
            lineHeight: 1.3
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            color: '#1E304B',
            fontSize: 16,
            height: 48,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#B8CDF3' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: Colors.primary.main },
            '&.Mui-focused': { boxShadow: '0 0 0 2px rgba(0, 143, 255, 0.14)' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: Colors.primary.main,
              borderWidth: 1
            },
            '&.Mui-disabled': { backgroundColor: '#F7F9FC' }
          },
          '& .MuiOutlinedInput-input': {
            padding: '12px 16px',
            '&::placeholder': { color: '#8090A8', opacity: 1 },
            '&.Mui-disabled': { WebkitTextFillColor: '#52647A' }
          },
          '& .MuiFormControl-root + .MuiTypography-root': {
            fontSize: 12,
            lineHeight: '16px',
            marginTop: '4px'
          }
        }}
      >
        <Box flex={1} minHeight={0} overflow="auto" px={4} pt={3} pb={4}>
          <Grid container columnSpacing={4} rowSpacing={3}>
            <Grid item xs={12}>
              <Grid container columnSpacing={4} rowSpacing={2.25}>
                <Grid item xs={12}>
                  <PatternInput
                    label="CPF"
                    name="cpf"
                    control={form.control}
                    placeholder="CPF"
                    format="###.###.###-##"
                  />
                  {cadastroNovo && (
                    <Typography
                      display="inline-block"
                      color={Colors.primary.main}
                      fontSize={12}
                      fontWeight={500}
                      mt={0.5}
                      px={1}
                      py={0.25}
                      borderRadius="12px"
                      bgcolor="rgba(0, 143, 255, 0.08)"
                    >
                      Cadastro não encontrado. Preencha os dados abaixo.
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} mt={cadastroNovo ? -0.75 : 0}>
                  <Input
                    name="nome"
                    control={form.control}
                    label="Nome do cliente"
                    placeholder="Nome do cliente"
                    inputProps={{ maxLength: 150 }}
                    disabled={desabilitarCampos}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Input
                    label="E-mail"
                    control={form.control}
                    placeholder="E-mail do cliente"
                    name="email"
                    disabled={desabilitarCampos}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
              </Grid>
            </Grid>
            {cadastroNovo && (
              <Grid item xs={12}>
                <Input
                  label="Confirmar e-mail"
                  control={form.control}
                  placeholder="Confirme o e-mail do cliente"
                  name="confirmarEmail"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
            )}
            <Grid item xs={12} mt={0.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography color="#283C58" fontSize={18} fontWeight={600}>
                  Cartão físico
                </Typography>
                <Divider flexItem sx={{ alignSelf: 'center', borderColor: '#D8E0EA', flex: 1 }} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                px={2}
                py={1.5}
                border="1px solid #D8E0EA"
                borderRadius="8px"
                bgcolor="#F7F9FC"
              >
                <Box>
                  <Typography color="#283C58" fontSize={15} fontWeight={600}>
                    Deseja associar um cartão físico?
                  </Typography>
                  <Typography color="#52647A" fontSize={13} mt={0.25}>
                    Informe o número apenas para cartões físicos. Cartões virtuais são gerados
                    automaticamente.
                  </Typography>
                </Box>
                <Controller
                  control={form.control}
                  name="associarCartao"
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      checked={value}
                      onChange={onChange}
                      inputProps={{ 'aria-label': 'Associar cartão físico' }}
                    />
                  )}
                />
              </Box>
            </Grid>
            {associarCartao && (
              <Grid item xs={12}>
                <FormCartao />
              </Grid>
            )}
          </Grid>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
          gap={4}
          px={4}
          py={3}
          bgcolor={Colors.white}
          borderTop="1px solid #D8E0EA"
          flexShrink={0}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: Colors.secondary,
              borderRadius: '8px',
              color: Colors.secondary,
              height: 48,
              textTransform: 'none'
            }}
          >
            Cancelar
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={form.handleSubmit(onValid, onInvalid)}
            sx={{
              bgcolor: Colors.secondary,
              borderRadius: '8px',
              boxShadow: 'none',
              height: 48,
              textTransform: 'none',
              '&:hover': { bgcolor: Colors.secondary, boxShadow: 'none' }
            }}
          >
            Confirmar associação
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default FormAssociarUsuario;
